import { useEffect, useState, useCallback } from "react";
import { CalendarClock, Check, Loader2, Unplug, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * CalendarConnect — a self-contained card users see in onboarding + Radar.
 * - When not connected: shows a Connect button that opens the Google OAuth flow
 * - When connected: shows connected email + last synced + a Disconnect action
 * - When ?calendar=connected|denied|error|expired is in the URL, shows a toast
 *   the first time it renders and then cleans the URL.
 */
export default function CalendarConnect({
  className = "",
  variant = "card",
  onChange,
}) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar/status");
      setStatus(data);
      onChange?.(data);
    } catch (e) {
      setStatus({ connected: false, configured: false });
    }
  }, [onChange]);

  useEffect(() => {
    load();
  }, [load]);

  // Handle post-oauth ?calendar= flags in URL exactly once
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const val = params.get("calendar");
    if (!val) return;
    if (val === "connected") toast.success("Google Calendar connected — syncing events.");
    else if (val === "denied") toast.info("Calendar connection was declined.");
    else if (val === "expired") toast.error("That connection link expired. Try again.");
    else toast.error("Could not connect calendar. Try again.");
    params.delete("calendar");
    const q = params.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (q ? `?${q}` : "")
    );
    load();
  }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/oauth/calendar/start");
      window.location.href = data.authorize_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start Google OAuth.";
      toast.error(typeof msg === "string" ? msg : "Could not start OAuth.");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Google Calendar? Real events will be removed from Radar.")) return;
    setBusy(true);
    try {
      await api.post("/calendar/disconnect");
      toast.success("Google Calendar disconnected.");
      await load();
    } catch (e) {
      toast.error("Could not disconnect.");
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      await api.get("/calendar/events?refresh=true");
      toast.success("Calendar re-synced.");
      await load();
    } catch (e) {
      toast.error("Sync failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <div
        className={`flex items-center gap-3 border border-rule bg-deep/40 p-4 font-mono text-[11px] uppercase tracking-[0.22em] text-mist ${className}`}
        data-testid="calendar-connect-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
        Reading calendar status…
      </div>
    );
  }

  if (!status.configured) {
    return (
      <div
        className={`border border-rule bg-deep/30 p-5 ${className}`}
        data-testid="calendar-connect-not-configured"
      >
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
          <CalendarClock className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
          Google Calendar
        </div>
        <p className="mt-3 text-sm text-mist">
          Calendar sync is not configured on this preview.
        </p>
      </div>
    );
  }

  // ---------- Connected state ----------
  if (status.connected) {
    return (
      <div
        className={`border border-rule bg-deep/40 p-5 md:p-6 ${className}`}
        data-testid="calendar-connect-connected"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-signalGreen">
              <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
              Google Calendar connected
            </div>
            <div className="mt-3 font-display text-lg text-ink md:text-xl">
              Reading events from{" "}
              <span className="font-mono text-teal">{status.email || "your Google account"}</span>
            </div>
            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
              {status.event_count} events cached ·{" "}
              {status.last_synced_at
                ? `last sync ${new Date(status.last_synced_at).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}`
                : "sync pending"}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!status.can_write && (
              <button
                onClick={connect}
                disabled={busy}
                data-testid="calendar-reconnect-write-btn"
                className="inline-flex items-center gap-2 border border-teal bg-teal/15 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-ink hover:bg-teal/25 disabled:opacity-40"
                title="Grant write access so Life OS can defend windows and add buffers on your calendar."
              >
                Reconnect · enable write
              </button>
            )}
            <button
              onClick={refresh}
              disabled={busy}
              data-testid="calendar-refresh-btn"
              className="border border-rule px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist hover:border-teal hover:text-ink disabled:opacity-40"
            >
              Re-sync
            </button>
            <button
              onClick={disconnect}
              disabled={busy}
              data-testid="calendar-disconnect-btn"
              className="inline-flex items-center gap-2 border border-signalRed/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-signalRed hover:border-signalRed hover:bg-signalRed/10 disabled:opacity-40"
            >
              <Unplug className="h-3 w-3" strokeWidth={1.5} />
              Disconnect
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Not connected ----------
  const inline = variant === "inline";
  return (
    <div
      className={`border ${inline ? "border-teal/60 bg-teal/5" : "border-rule bg-deep/40"} p-5 md:p-6 ${className}`}
      data-testid="calendar-connect-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-teal">
            <CalendarClock className="h-3.5 w-3.5" strokeWidth={1.5} />
            Optional · Google Calendar
          </div>
          <div className="mt-3 font-display text-xl text-ink md:text-2xl">
            Let the tower see the same horizon you see.
          </div>
          <p className="mt-3 text-sm leading-relaxed text-mist">
            Connect your Google Calendar and Life OS will fold the next 30 days
            of events into your Capacity, Compression, and Recovery signals —
            and Radar will show what&apos;s inbound. Read-only. You can
            disconnect any time.
          </p>
        </div>
        <button
          onClick={connect}
          disabled={busy}
          data-testid="calendar-connect-btn"
          className="inline-flex items-center gap-2 bg-teal px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-teal-hover disabled:opacity-40"
        >
          {busy ? "Redirecting…" : "Connect Google Calendar"}
          <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
